import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { canDeleteAssignedVacation, type VacationRequest } from '@domain/entities/VacationRequest';
import type { Person } from '@domain/entities/Person';
import { getServices } from '@infrastructure/services';
import { Banner, Button, Card, Chip, Modal } from '../../design-system';
import { formatDateRange, reasonFor, statusChipColors } from '../../format';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import styles from './AssignedVacationsCard.module.css';

/**
 * Las vacaciones que **quien mira** registró a esta persona, y el botón de
 * borrarlas.
 *
 * ## Por qué vive en la ficha y no en el tablero de aprobaciones
 *
 * Porque es donde se registran. `AssignVacationAction` está tres tarjetas más
 * arriba, y quien acaba de equivocarse de fechas busca deshacerlo donde lo hizo.
 *
 * Y porque **el superusuario no tiene «Aprobaciones» en su menú**: su navegación
 * es la de configurar la plataforma. El tablero además exige el permiso
 * `aprobar`, que RR.HH. no tiene aunque sí pueda registrar —`admin` o `areas`—,
 * así que un botón allí habría quedado fuera del alcance de justo las dos
 * personas que registran.
 *
 * ## Por qué sólo lista las propias
 *
 * No es una vista del historial de la persona: es la lista de lo que quien mira
 * puede deshacer. Enseñar aquí lo que registró otro administrador sería ofrecer
 * un botón que el servidor va a rechazar. Ver `canDeleteAssignedVacation`.
 *
 * La tarjeta **no se dibuja si no hay nada**: un hueco permanente en la ficha de
 * las 335 personas a las que nadie ha registrado nada no dice nada útil.
 */
export const AssignedVacationsCard = ({ person }: { readonly person: Person }): ReactNode => {
  const session = useSession();
  const [deleting, setDeleting] = useState<VacationRequest | null>(null);

  const registradas = useQuery({
    queryKey: ['assignedVacations', person.id, session.personId],
    queryFn: async () => {
      const result = await getServices().requests.list(
        { personId: person.id },
        /* Cincuenta de sobra: en producción hay tres registradas en todo el
           padrón, y la lista ya viene filtrada a las de quien mira. */
        { limit: 50 },
      );
      if (!result.ok) throw new Error('No pudimos leer sus vacaciones.');
      return result.value.items.filter((request) =>
        canDeleteAssignedVacation(request, session.personId),
      );
    },
  });

  /*
   * Mientras carga no se dibuja nada, y es deliberado.
   *
   * Casi siempre la lista va a estar vacía —tres solicitudes registradas en todo
   * el padrón— así que un esqueleto o un «Cargando» aparecería y desaparecería en
   * cada ficha que alguien abra, prometiendo algo que casi nunca llega.
   */
  if (registradas.isPending) return null;

  /*
   * Un fallo de lectura sí se dice. Callarlo dejaría la tarjeta ausente, que aquí
   * significa «no registraste nada» — y eso es una afirmación, no un hueco. Es la
   * pantalla correcta y vacía que este repositorio ya rastrea.
   */
  if (registradas.isError) {
    return (
      <Card>
        <Banner tone="warn">
          No pudimos leer las vacaciones que registraste a {person.displayName}. Vuelve a cargar la
          página antes de registrar otras, para no duplicarlas.
        </Banner>
      </Card>
    );
  }

  const lista = registradas.data ?? [];
  if (lista.length === 0) return null;

  return (
    <Card>
      <div className={styles.head}>
        <h2 className={styles.title}>Vacaciones que le registraste</h2>
        <p className={styles.note}>
          Sólo aparecen las que registraste tú. Puedes borrarlas: los días vuelven a su saldo y no
          queda constancia en el portal, sólo en la bitácora.
        </p>
      </div>

      <ul className={styles.list}>
        {lista.map((request) => {
          const colors = statusChipColors(request.status);
          return (
            <li key={request.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.folio}>{request.folio}</span>
                <span className={styles.range}>{formatDateRange(request.range)}</span>
                <span className={styles.days}>
                  {String(request.businessDays)} día(s) hábil(es)
                </span>
              </div>
              <div className={styles.rowSide}>
                <Chip background={colors.background} foreground={colors.foreground}>
                  {request.status}
                </Chip>
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => {
                    setDeleting(request);
                  }}
                >
                  Borrar
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {deleting !== null && (
        <DeleteAssignedDialog
          request={deleting}
          personName={person.displayName}
          onClose={() => {
            setDeleting(null);
          }}
          onDone={async () => {
            setDeleting(null);
            await registradas.refetch();
          }}
        />
      )}
    </Card>
  );
};

/**
 * Confirmar el borrado. Nunca de un solo clic.
 *
 * **Sin motivo del catálogo, a diferencia de cancelar.** Cancelar deja un hueco
 * en el plan de la sub área que alguien tendrá que entender después, y por eso
 * pide explicarlo. Aquí no queda hueco: el registro se va entero.
 *
 * Lo que sí hace falta es que quien pulsa sepa qué se lleva por delante, porque
 * **no hay pantalla donde volver a mirarlo**.
 */
const DeleteAssignedDialog = ({
  request,
  personName,
  onClose,
  onDone,
}: {
  readonly request: VacationRequest;
  readonly personName: string;
  readonly onClose: () => void;
  readonly onDone: () => Promise<void>;
}): ReactNode => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      title={`Borrar ${request.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            busy={busy}
            onClick={() => {
              setBusy(true);
              void getServices()
                .requests.deleteAssigned(request.id)
                .then(async (result) => {
                  setBusy(false);
                  if (!result.ok) {
                    toast.bad(`No se pudo borrar ${request.folio}.${reasonFor(result.error)}`);
                    return;
                  }
                  toast.ok(`${request.folio} se borró y los días volvieron a su saldo.`);
                  await onDone();
                });
            }}
          >
            Borrar
          </Button>
        </>
      }
    >
      <div className={styles.dialog}>
        <p className={styles.note}>
          Le registraste estas vacaciones a {personName} · {formatDateRange(request.range)} ·{' '}
          {String(request.businessDays)} día(s) hábil(es), y están en «{request.status}». Al
          borrarlas desaparecen del portal y esos días vuelven a su saldo.
        </p>
        {/* Dicho antes de pulsar: es la diferencia con cancelar, y la única razón
            por la que alguien elegiría una u otra. */}
        <Banner tone="warn">
          No queda registro visible: a diferencia de cancelar, no se guarda ninguna fila con el
          folio. Sólo la bitácora conserva qué se borró y quién.
        </Banner>
      </div>
    </Modal>
  );
};
